import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { trackPageView, trackFAQInteraction, trackCTAClick, trackInternalLink } from '../utils/analytics';

const content = {
  en: {
    // SEO Metadata
    title: 'Where to Get Specialized Asian Skin Treatment? | Halo Aesthetic Clinic',
    description: 'Looking for specialized treatment for Asian skin? Halo Aesthetic Clinic offers expert care tailored to Asian skin types. Book your consultation for personalized skincare solutions.',
    
    // Main Question (H1)
    mainQuestion: 'Where to Get Specialized Asian Skin Treatment?',
    
    // Supporting Sections (H2)
    intro: 'Understanding Asian Skin Characteristics',
    introText: 'Asian skin has unique characteristics that require specialized treatment approaches. Our experienced practitioners understand the specific needs of Asian skin types, including sensitivity to certain treatments, pigmentation concerns, and texture differences.',
    
    treatmentsTitle: 'Specialized Treatments for Asian Skin',
    treatmentsText: 'We offer comprehensive treatments specifically designed for Asian skin, addressing common concerns such as hyperpigmentation, melasma, acne scarring, and maintaining skin elasticity.',
    
    expertiseTitle: 'Why Choose Our Specialists',
    expertiseText: 'Our team has extensive experience with Asian skin types and understands the nuances of treating Asian skin safely and effectively. We use advanced techniques and products specifically formulated for Asian skin concerns.',
    
    consultationTitle: 'Book Your Consultation',
    consultationText: 'Ready to discover the best treatment plan for your Asian skin? Schedule a consultation with our specialists for a personalized approach tailored to your unique needs.',
    
    ctaButton: 'Book Consultation',
    
    // FAQ Placeholder
    faqTitle: 'Frequently Asked Questions',
    faqPlaceholder: 'FAQ content will be added here with common questions about Asian skin treatment, suitable procedures, expected results, and aftercare recommendations.',
  },
  cn: {
    // SEO Metadata
    title: '哪里可以做亚洲皮肤专业治疗？| Halo医美中心',
    description: '寻找针对亚洲皮肤的专业治疗？Halo医美中心提供专为亚洲肤质定制的专业护理。预约咨询，获得个性化皮肤护理方案。',
    
    // Main Question (H1)
    mainQuestion: '哪里可以做亚洲皮肤专业治疗？',
    
    // Supporting Sections (H2)
    intro: '了解亚洲皮肤特性',
    introText: '亚洲皮肤具有独特的特性，需要专业的治疗方法。我们经验丰富的医师了解亚洲肤质的特殊需求，包括对某些治疗的敏感性、色素沉着问题和质地差异。',
    
    treatmentsTitle: '针对亚洲皮肤的专业治疗',
    treatmentsText: '我们提供专门为亚洲皮肤设计的全面治疗方案，解决色素沉着、黄褐斑、痤疮疤痕等常见问题，并保持皮肤弹性。',
    
    expertiseTitle: '为什么选择我们的专家',
    expertiseText: '我们的团队在处理亚洲肤质方面拥有丰富经验，了解安全有效治疗亚洲皮肤的细微差别。我们使用专门为亚洲皮肤问题配制的先进技术和产品。',
    
    consultationTitle: '预约咨询',
    consultationText: '准备好为您的亚洲皮肤发现最佳治疗方案了吗？与我们的专家预约咨询，获得针对您独特需求的个性化治疗方法。',
    
    ctaButton: '预约咨询',
    
    // FAQ Placeholder
    faqTitle: '常见问题',
    faqPlaceholder: '常见问题内容将在此添加，包括关于亚洲皮肤治疗、适合的程序、预期效果和护理建议的常见问题。',
  }
};

const AsianSkinTreatment = () => {
  const { lang } = useLang();
  const c = content[lang];
  const location = useLocation();
  const hasTrackedPageView = useRef(false);

  // 跟踪页面浏览和核心问题展示
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPageView(location.pathname, c.title, c.mainQuestion);
      hasTrackedPageView.current = true;
    }
  }, [location.pathname, c.title, c.mainQuestion]);

  // FAQ交互追踪处理函数
  const handleFAQToggle = (question, isOpen) => {
    trackFAQInteraction(question, isOpen ? 'expand' : 'collapse', location.pathname);
  };

  // CTA点击追踪处理函数
  const handleCTAClick = () => {
    trackCTAClick(c.ctaButton, 'hero', location.pathname);
  };

  // 内部链接点击追踪处理函数
  const handleInternalLinkClick = (linkText, linkPath) => {
    trackInternalLink(linkText, linkPath, location.pathname);
  };

  // JSON-LD Structured Data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.haloaestheticclinic.com/asian-skin-treatment#webpage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": lang === 'en'
          ? "What treatments are best for Asian skin types?"
          : "什么治疗最适合亚洲肤质？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Asian skin requires specialized care due to unique characteristics like higher melanin content and sensitivity. We offer customized treatments for hyperpigmentation, melasma, and gentle skincare regimens."
            : "亚洲皮肤由于独特的特性（如较高的黑色素含量和敏感性）需要专业护理。我们提供针对色素沉着、黄褐斑和温和护肤方案的定制治疗。"
        }
      },
      {
        "@type": "Question",
        "name": lang === 'en'
          ? "Are aesthetic treatments safe for Asian skin?"
          : "医美治疗对亚洲皮肤安全吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Safety depends on individual skin characteristics and medical history. We specialize in Asian skin treatments and customize all procedures to your specific needs. A comprehensive consultation ensures safe and effective treatment planning."
            : "安全性取决于个人皮肤特征和医疗史。我们专门从事亚洲皮肤治疗，并根据您的特定需求定制所有程序。全面咨询确保安全有效的治疗计划。"
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": "https://www.haloaestheticclinic.com/#organization",
    "name": "Halo Aesthetic Clinic",
    "url": "https://www.haloaestheticclinic.com",
    "image": "https://www.haloaestheticclinic.com/logo.jpg",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.haloaestheticclinic.com/logo.jpg"
    },
    "telephone": "+14377555409",
    "email": "info@haloaestheticclinic.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1595 16th Ave Suite 103",
      "addressLocality": "Richmond Hill",
      "addressRegion": "ON",
      "postalCode": "L4B 3N9",
      "addressCountry": "CA"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Tuesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Wednesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Thursday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Friday",
        "opens": "13:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "19:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "medicalSpecialty": "Dermatology"
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": c.mainQuestion,
    "description": c.description,
    "author": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "publisher": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.haloaestheticclinic.com/asian-skin-treatment"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": lang === 'en' ? "Asian Skin Specialized Treatment" : "亚洲皮肤专业治疗",
    "serviceType": "Asian Skin Treatment",
    "description": lang === 'en'
      ? "Specialized aesthetic treatments designed specifically for Asian skin types. Includes hyperpigmentation treatment, melasma solutions, and gentle skincare regimens tailored to Asian skin characteristics."
      : "专为亚洲肤质设计的专业医美治疗。包括色素沉着治疗、黄褐斑解决方案和针对亚洲皮肤特性的温和护肤方案。",
    "url": "https://www.haloaestheticclinic.com/asian-skin-treatment",
    "provider": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "category": "Medical Aesthetic Treatment",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.haloaestheticclinic.com/asian-skin-treatment",
      "servicePhone": "+14377555409"
    }
  };

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
        <meta name="description" content={c.description} />
        <link rel="canonical" href="https://www.haloaestheticclinic.com/asian-skin-treatment" />
        <meta name="keywords" content={lang === 'en'
          ? 'Asian skin treatment, specialized Asian skincare, Asian skin clinic, hyperpigmentation treatment, melasma treatment'
          : '亚洲皮肤治疗, 专业亚洲护肤, 亚洲皮肤诊所, 色素沉着治疗, 黄褐斑治疗'
        } />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>
      
      <div className="bg-softGradient bg-no-repeat bg-top text-text">
        {/* Hero Section with Main Question */}
        <section className="py-16 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {c.mainQuestion}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {c.introText}
            </p>
          </div>
        </section>

        {/* Supporting Section 1: Treatments */}
        <section className="py-16 bg-white/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 text-center">{c.treatmentsTitle}</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              {c.treatmentsText}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Hyperpigmentation Treatment' : '色素沉着治疗'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Advanced treatments to reduce dark spots and uneven skin tone common in Asian skin.'
                    : '先进的治疗，减少亚洲皮肤常见的黑斑和肤色不均。'
                  }
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Melasma Solutions' : '黄褐斑解决方案'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Specialized protocols for treating melasma, a common concern for Asian skin types.'
                    : '治疗黄褐斑的专业方案，这是亚洲肤质的常见问题。'
                  }
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Gentle Skincare Regimens' : '温和护肤方案'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Customized skincare routines designed for sensitive Asian skin with proper hydration and protection.'
                    : '为敏感的亚洲皮肤设计的定制护肤程序，提供适当的水分和保护。'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Supporting Section 2: Expertise & Consultation */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">{c.expertiseTitle}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {c.expertiseText}
              </p>
              <Link
                to="/book-form"
                onClick={handleCTAClick}
                className="inline-block px-8 py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60 text-lg font-semibold"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
                }}
                aria-label={lang === 'en' ? 'Book your Asian skin treatment consultation' : '预约亚洲皮肤治疗咨询'}
              >
                {c.ctaButton}
              </Link>
            </div>
            
            {/* Related Services Section */}
            <div className="mt-16 bg-white/70 backdrop-blur-sm rounded-xl p-8 border border-champagne/40">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">
                {lang === 'en' ? 'Explore More Services' : '探索更多服务'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link 
                  to="/after-hours-services"
                  onClick={() => handleInternalLinkClick(
                    lang === 'en' ? 'Friday After-Hours Services' : '周五延长服务时间',
                    '/after-hours-services'
                  )}
                  className="block p-6 rounded-lg border border-champagne/40 hover:border-[#2E6B4C] hover:shadow-glow transition-all bg-white/50"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {lang === 'en' ? 'Friday After-Hours Services' : '周五延长服务时间'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lang === 'en' 
                      ? 'Convenient Friday evening appointments from 1PM to 9PM.'
                      : '方便的周五晚上预约，从下午1点到晚上9点。'
                    }
                  </p>
                </Link>
                <Link 
                  to="/ulthera-richmond-hill"
                  onClick={() => handleInternalLinkClick(
                    lang === 'en' ? 'Ulthera Prime Treatment' : 'Ulthera Prime治疗',
                    '/ulthera-richmond-hill'
                  )}
                  className="block p-6 rounded-lg border border-champagne/40 hover:border-[#2E6B4C] hover:shadow-glow transition-all bg-white/50"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {lang === 'en' ? 'Ulthera Prime Treatment' : 'Ulthera Prime治疗'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lang === 'en' 
                      ? 'Non-invasive skin tightening in Richmond Hill.'
                      : '在Richmond Hill提供的非侵入性皮肤紧致治疗。'
                    }
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-softRadial">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">{c.faqTitle}</h2>
            <div className="space-y-4">
              <details 
                className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                onToggle={(e) => {
                  const question = lang === 'en' ? 'Why do Asian skin types need specialized treatment?' : '为什么亚洲肤质需要专业治疗？';
                  handleFAQToggle(question, e.target.open);
                }}
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                  <span className="flex items-center justify-between">
                    <span>{lang === 'en' ? 'Why do Asian skin types need specialized treatment?' : '为什么亚洲肤质需要专业治疗？'}</span>
                    <svg className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                  <p>
                    {lang === 'en' 
                      ? 'Asian skin has unique characteristics including higher melanin content, increased sensitivity to certain treatments, and different texture. Specialized protocols ensure safe and effective results tailored to these specific needs.'
                      : '亚洲皮肤具有独特的特性，包括较高的黑色素含量、对某些治疗敏感性增加以及不同的质地。专业方案确保针对这些特定需求的安全有效结果。'
                    }
                  </p>
                </div>
              </details>
              
              <details 
                className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                onToggle={(e) => {
                  const question = lang === 'en' ? 'What treatments are most effective for Asian skin?' : '什么治疗对亚洲皮肤最有效？';
                  handleFAQToggle(question, e.target.open);
                }}
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                  <span className="flex items-center justify-between">
                    <span>{lang === 'en' ? 'What treatments are most effective for Asian skin?' : '什么治疗对亚洲皮肤最有效？'}</span>
                    <svg className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                  <p>
                    {lang === 'en' 
                      ? 'Effective treatments for Asian skin include hyperpigmentation therapy, melasma solutions, and gentle skincare regimens. Our specialists customize each treatment plan based on individual skin characteristics and concerns.'
                      : '对亚洲皮肤有效的治疗包括色素沉着治疗、黄褐斑解决方案和温和护肤方案。我们的专家根据个人皮肤特征和问题定制每个治疗计划。'
                    }
                  </p>
                </div>
              </details>
              
              <div className="text-center mt-6">
                <Link 
                  to="/faq" 
                  className="text-[#2E6B4C] font-semibold hover:underline"
                  aria-label={lang === 'en' ? 'View all frequently asked questions' : '查看所有常见问题'}
                >
                  {lang === 'en' ? 'View All FAQs →' : '查看所有常见问题 →'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AsianSkinTreatment;

